import { createClient } from '@supabase/supabase-js'

// KML's personal Supabase instance - hardcoded for convenience
const HARDCODED_URL = 'https://yxsvdkfdwigtlqjihbce.supabase.co'
const HARDCODED_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl4c3Zka2Zkd2lndGxxamloYmNlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzAwMDg1MTcsImV4cCI6MjA4NTU4NDUxN30.2zYahw3waI-9Kud8D6Oj5MnCoUCXJQQNZygSqcbzen8'

// Load config from localStorage FIRST (before createClient)
const storedUrl = localStorage.getItem('supabase_url')
const storedKey = localStorage.getItem('supabase_key')

// Resolve URL and key: hardcoded > localStorage > env vars > placeholder
const supabaseUrl = HARDCODED_URL || storedUrl || import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co'
const supabaseAnonKey = HARDCODED_KEY || storedKey || import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder'

// Client-side Supabase (limited permissions through RLS)
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Helper function to check if Supabase is configured
// Always true when hardcoded credentials are present
export const isSupabaseConfigured = () => {
  return true // Hardcoded for KML's personal instance
}

// Helper function to update Supabase configuration at runtime
export const updateSupabaseConfig = (url: string, key: string) => {
  localStorage.setItem('supabase_url', url)
  localStorage.setItem('supabase_key', key)
  window.location.reload()
}

// Common query helpers
export const supabaseHelpers = {
  async checkConnection() {
    try {
      const { error } = await supabase.from('domains').select('id').limit(1)
      return !error
    } catch {
      return false
    }
  },

  async getDomains() {
    const { data, error } = await supabase
      .from('domains')
      .select('*')
      .order('name')
    
    if (error) throw error
    return data
  },

  async getIngestions(limit = 10) {
    const { data, error } = await supabase
      .from('ingestions')
      .select(`
        *,
        primary_domain:domains(*)
      `)
      .order('ingested_at', { ascending: false })
      .limit(limit)
    
    if (error) throw error
    return data
  },

  async getConcepts(limit = 50) {
    const { data, error } = await supabase
      .from('concepts')
      .select(`
        *,
        domain:domains(*),
        connection_count:connections(count)
      `)
      .order('synapse_strength', { ascending: false })
      .limit(limit)
    
    if (error) throw error
    return data
  },

  async searchSimilar(query: string, limit = 10, threshold = 0.5) {
    const { data, error } = await supabase
      .rpc('search_similar', {
        query_text: query,
        match_count: limit,
        match_threshold: threshold
      })
    
    if (error) throw error
    return data
  },

  async getHealthScore() {
    const { data, error } = await supabase
      .from('health_scores')
      .select('*')
      .order('calculated_at', { ascending: false })
      .limit(1)
      .single()
    
    if (error) throw error
    return data
  },

  async getEmergences() {
    const { data, error } = await supabase
      .from('emergences')
      .select('*')
      .eq('status', 'new')
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return data
  },

  async getLenses() {
    const { data, error } = await supabase
      .from('lenses')
      .select('*')
      .eq('is_active', true)
      .order('lens_type')
    
    if (error) throw error
    return data
  },

  async updateLens(lensType: string, content: string) {
    const { data, error } = await supabase
      .from('lenses')
      .upsert({
        lens_type: lensType,
        content,
        is_active: true,
        version: 1,
        updated_at: new Date().toISOString()
      })
      .eq('lens_type', lensType)
    
    if (error) throw error
    return data
  },

  async getGraphData() {
    // Get concepts with their connections
    const { data: concepts, error: conceptsError } = await supabase
      .from('concepts')
      .select(`
        id,
        name,
        domain_id,
        complexity_level,
        synapse_strength,
        access_count,
        domain:domains(name, color)
      `)
      .eq('mesh_eligible', true)

    if (conceptsError) throw conceptsError

    // Get connections between mesh-eligible concepts
    const conceptIds = concepts.map(c => c.id)
    const { data: connections, error: connectionsError } = await supabase
      .from('connections')
      .select('*')
      .in('from_node_id', conceptIds)
      .in('to_node_id', conceptIds)
      .eq('from_node_type', 'concept')
      .eq('to_node_type', 'concept')

    if (connectionsError) throw connectionsError

    return { concepts, connections }
  }
}